import { useState, useEffect } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

export function ProVaultPanel() {
  const blank = { title: "", description: "", category: "Guide", max_downloads: 10, order: 0, published: true };
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState({ count: 0, active_count: 0, members: [] });
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [it, mem] = await Promise.all([
        api.get("/admin/pro-vault/items"),
        api.get("/admin/pro-vault/members"),
      ]);
      setItems(it.data.items || []);
      setMembers(mem.data || { count: 0, active_count: 0, members: [] });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load Pro Vault");
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || null,
        category: form.category || "Guide",
        max_downloads: Math.max(1, parseInt(form.max_downloads, 10) || 10),
        order: parseInt(form.order, 10) || 0,
        published: !!form.published,
      };
      if (editingId) {
        await api.put(`/admin/pro-vault/items/${editingId}`, payload);
        toast.success("Resource updated.");
        reset();
        await load();
      } else {
        const { data } = await api.post("/admin/pro-vault/items", payload);
        toast.success("Resource created. Now upload the file + cover →");
        setEditingId(data.id);
        await load();
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This removes the resource and its file.`)) return;
    try {
      await api.delete(`/admin/pro-vault/items/${id}`);
      toast.success("Deleted.");
      if (editingId === id) reset();
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      title: it.title || "",
      description: it.description || "",
      category: it.category || "Guide",
      max_downloads: it.max_downloads || 10,
      order: it.order || 0,
      published: it.published !== false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editingItem = items.find((i) => i.id === editingId);

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="surface p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">Resources</p>
          <p className="font-serif text-3xl mt-1" style={{ color: "var(--gold)" }}>{items.length}</p>
        </div>
        <div className="surface p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">Active members</p>
          <p className="font-serif text-3xl mt-1" style={{ color: "var(--gold)" }}>{members.active_count}</p>
        </div>
        <div className="surface p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/45">Est. MRR</p>
          <p className="font-serif text-3xl mt-1" style={{ color: "var(--gold)" }}>${(members.active_count * 6.5).toFixed(2)}</p>
        </div>
      </div>

      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-white/55">$6.50/mo Pro Vault · industry downloads</p>
        <Button
          onClick={() => {
            if (showForm) reset();
            else {
              setForm({ ...blank, order: items.length + 1 });
              setEditingId(null);
              setShowForm(true);
            }
          }}
          className="btn-gold rounded-none px-6 py-4 text-xs"
          data-testid="admin-new-vault-item"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Close" : "New Resource"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6" data-testid="vault-form">
          <Field label="Title" full>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="vault-title"
            />
          </Field>
          <Field label="Category">
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-transparent border-b border-white/15 h-11 outline-none w-full text-sm"
              data-testid="vault-category"
            >
              {["Guide", "Template", "EPK Asset", "Checklist", "Contract", "Resource"].map((c) => (
                <option key={c} value={c} style={{ background: "#1a0d12" }}>{c}</option>
              ))}
            </select>
          </Field>
          <Field label="Download cap (per member, per file)">
            <Input
              type="number"
              min={1}
              value={form.max_downloads}
              onChange={(e) => setForm({ ...form, max_downloads: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="vault-cap"
            />
          </Field>
          <Field label="Sort order">
            <Input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="vault-order"
            />
          </Field>
          <Field label="Published">
            <select
              value={form.published ? "yes" : "no"}
              onChange={(e) => setForm({ ...form, published: e.target.value === "yes" })}
              className="bg-transparent border-b border-white/15 h-11 outline-none w-full text-sm"
              data-testid="vault-published"
            >
              <option value="yes" style={{ background: "#1a0d12" }}>Published (live)</option>
              <option value="no" style={{ background: "#1a0d12" }}>Draft (hidden)</option>
            </select>
          </Field>
          <Field label="Description" full>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={1200}
              className="w-full bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] p-3 text-sm resize-y outline-none"
              data-testid="vault-description"
            />
          </Field>

          {editingId && (
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-8 border-t border-white/5 pt-6">
              <VaultFileUploader itemId={editingId} item={editingItem} onUploaded={load} />
              <VaultCoverUploader itemId={editingId} currentCover={editingItem?.cover_image} onUploaded={load} />
            </div>
          )}

          <div className="sm:col-span-2 flex gap-4 pt-2 border-t border-white/5">
            <Button type="submit" disabled={busy} className="btn-gold rounded-none px-7 py-3 text-xs" data-testid="vault-submit">
              {busy ? "Saving…" : editingId ? "Save Changes" : "Create Resource"}
            </Button>
            {editingId && (
              <Button type="button" onClick={reset} className="bg-transparent border border-white/15 rounded-none px-5 py-3 text-xs hover:border-white/40">
                Done
              </Button>
            )}
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="vault-list">
        {items.length === 0 ? (
          <p className="text-sm text-white/50">No resources yet. Create one and upload the file + cover.</p>
        ) : (
          items.map((it) => (
            <div key={it.id} className="surface p-5 flex items-center gap-4 flex-wrap" data-testid={`vault-row-${it.slug}`}>
              <div
                className="w-16 h-16 bg-cover bg-center shrink-0 border border-white/10"
                style={{ backgroundImage: it.cover_image ? `url(${imgUrl(it.cover_image)})` : "linear-gradient(135deg, #2a1822, #1a0d12)" }}
              />
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong style={{ color: "var(--gold)" }}>{it.title}</strong>
                  <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-white/15 text-white/55">{it.category}</span>
                  <span
                    className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border"
                    style={{
                      color: it.published ? "#9ddf8b" : "rgba(255,255,255,0.4)",
                      borderColor: it.published ? "rgba(157,223,139,0.4)" : "rgba(255,255,255,0.15)",
                    }}
                  >
                    {it.published ? "live" : "draft"}
                  </span>
                  {!it.file_path && (
                    <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-red-400/40 text-red-300/80">No file</span>
                  )}
                </div>
                <div className="text-[11px] text-white/45 mt-1">
                  {it.file_ext ? `${it.file_ext.toUpperCase()} · ` : ""}cap {it.max_downloads}/file · {it.slug}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => startEdit(it)} className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] hover:opacity-80" data-testid={`vault-edit-${it.slug}`}>
                  <Edit3 size={11} className="inline mr-1" />Edit
                </button>
                <button onClick={() => remove(it.id, it.title)} className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-400" data-testid={`vault-delete-${it.slug}`}>
                  <Trash2 size={11} className="inline mr-1" />Del
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function VaultFileUploader({ itemId, item, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/pro-vault/items/${itemId}/file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded.");
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Downloadable File (PDF / PNG / JPG / ZIP · max 50 MB)</Label>
      {item?.file_path && (
        <p className="text-[11px] text-white/50">
          Current: {item.file_ext?.toUpperCase()} · {((item.file_size || 0) / (1024 * 1024)).toFixed(1)} MB
        </p>
      )}
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg,image/webp,application/zip"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
        data-testid="vault-file-upload"
      />
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}

function VaultCoverUploader({ itemId, currentCover, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/pro-vault/items/${itemId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded.");
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Cover upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Preview Cover (JPG / PNG / WebP)</Label>
      <div className="flex items-center gap-3">
        {currentCover && <img src={imgUrl(currentCover)} alt="Cover" className="w-14 h-14 object-cover border border-white/10" />}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
          className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
          data-testid="vault-cover-upload"
        />
      </div>
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}
